import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import UndoIcon from '@mui/icons-material/Undo';
import PersonIcon from '@mui/icons-material/Person';
import { useDashboard } from '../hooks/useDashboard.js';
import {
  useCheckInStats,
  useManualCheckIn,
  useUndoCheckIn,
  useQrCheckIn,
} from '../hooks/useCheckin.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FilterMode = 'all' | 'checked-in' | 'not-checked-in';

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function CheckInSkeleton() {
  return (
    <Stack spacing={2} p={2}>
      <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function CheckInPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: dashboard, isLoading: dashLoading, isError: dashError, error: dashErr } = useDashboard(eventId ?? '');
  const { data: stats, isLoading: statsLoading } = useCheckInStats(eventId);
  const manualCheckIn = useManualCheckIn();
  const undoCheckIn = useUndoCheckIn();
  const qrCheckIn = useQrCheckIn();

  const [search, setSearch] = useState('');
  const [qrInput, setQrInput] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Filter & search guests
  const filteredGuests = useMemo(() => {
    if (!dashboard) return [];
    let guests = [...dashboard.guests];

    // Apply filter
    if (filter === 'checked-in') {
      guests = guests.filter((g) => !!(g as unknown as { checkedInAt?: string | null }).checkedInAt);
    } else if (filter === 'not-checked-in') {
      guests = guests.filter((g) => !(g as unknown as { checkedInAt?: string | null }).checkedInAt);
    }

    // Apply search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      guests = guests.filter(
        (g) =>
          g.name.toLowerCase().includes(q) ||
          g.email.toLowerCase().includes(q),
      );
    }

    return guests.sort((a, b) => a.name.localeCompare(b.name));
  }, [dashboard, filter, search]);

  const handleManualCheckIn = async (guestId: string, guestName: string) => {
    if (!eventId) return;
    setFeedback(null);
    try {
      await manualCheckIn.mutateAsync({ eventId, guestId });
      setFeedback({ type: 'success', message: `${guestName} checked in!` });
    } catch (err) {
      setFeedback({ type: 'error', message: (err as Error)?.message ?? 'Check-in failed' });
    }
  };

  const handleUndoCheckIn = async (guestId: string, guestName: string) => {
    if (!eventId) return;
    setFeedback(null);
    try {
      await undoCheckIn.mutateAsync({ eventId, guestId });
      setFeedback({ type: 'success', message: `${guestName} check-in undone` });
    } catch (err) {
      setFeedback({ type: 'error', message: (err as Error)?.message ?? 'Undo failed' });
    }
  };

  const handleQrSubmit = async () => {
    if (!eventId || !qrInput.trim()) return;
    setFeedback(null);
    try {
      const result = await qrCheckIn.mutateAsync({ eventId, qrCode: qrInput.trim() });
      setFeedback({ type: 'success', message: `${result.guest.name} checked in via QR!` });
      setQrInput('');
    } catch (err) {
      setFeedback({ type: 'error', message: (err as Error)?.message ?? 'QR check-in failed' });
    }
  };

  // Helper: check if a guest from dashboard data is checked in
  // The dashboard endpoint may not include checkedInAt directly,
  // so we rely on refetching after mutations.
  type DashboardGuest = NonNullable<typeof dashboard>['guests'][number];
  const isGuestCheckedIn = (guest: DashboardGuest) => {
    return !!(guest as unknown as { checkedInAt?: string | null }).checkedInAt;
  };

  // Loading
  if (dashLoading) {
    return <CheckInSkeleton />;
  }

  // Error
  if (dashError) {
    return (
      <Box p={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">
          {(dashErr as Error)?.message ?? 'Failed to load event'}
        </Alert>
      </Box>
    );
  }

  if (!dashboard) {
    return (
      <Box p={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="warning">Event not found.</Alert>
      </Box>
    );
  }

  const { event } = dashboard;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', pb: 4 }}>
      {/* ── Header ── */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ p: 2, pb: 1 }}
      >
        <IconButton onClick={() => navigate(`/events/${eventId}`)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1} minWidth={0}>
          <Typography variant="h6" fontWeight={700} noWrap>
            {event.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Check-In
          </Typography>
        </Box>
      </Stack>

      {/* ── Stats Bar ── */}
      <Card sx={{ mx: 2, mb: 2 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          {statsLoading ? (
            <Skeleton variant="rectangular" height={40} />
          ) : stats ? (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  {stats.checkedIn} / {stats.total}
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  {stats.checkedInPercent}% checked in
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={stats.checkedInPercent}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No stats available
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* ── QR Code Input ── */}
      <Box sx={{ px: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="medium"
          placeholder="Scan or paste QR code..."
          value={qrInput}
          onChange={(e) => setQrInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleQrSubmit();
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <QrCodeScannerIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: qrInput.trim() ? (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleQrSubmit}
                    disabled={qrCheckIn.isPending}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    {qrCheckIn.isPending ? <CircularProgress size={18} color="inherit" /> : 'Go'}
                  </Button>
                </InputAdornment>
              ) : undefined,
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              bgcolor: 'background.paper',
            },
          }}
        />
      </Box>

      {/* ── Search Bar ── */}
      <Box sx={{ px: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="medium"
          placeholder="Search guests by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              bgcolor: 'background.paper',
            },
          }}
        />
      </Box>

      {/* ── Filter Toggles ── */}
      <Box sx={{ px: 2, mb: 2 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_e, val: FilterMode | null) => {
            if (val) setFilter(val);
          }}
          fullWidth
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              py: 1,
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="checked-in">Checked In</ToggleButton>
          <ToggleButton value="not-checked-in">Not In</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* ── Feedback ── */}
      {feedback && (
        <Box sx={{ px: 2, mb: 2 }}>
          <Alert
            severity={feedback.type}
            onClose={() => setFeedback(null)}
            sx={{ borderRadius: 2 }}
          >
            {feedback.message}
          </Alert>
        </Box>
      )}

      {/* ── Guest List ── */}
      <Card sx={{ mx: 2, borderRadius: 3 }}>
        {filteredGuests.length === 0 ? (
          <CardContent>
            <Stack alignItems="center" py={4}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                {search.trim() ? 'No guests match your search' : 'No guests to show'}
              </Typography>
            </Stack>
          </CardContent>
        ) : (
          <List disablePadding>
            {filteredGuests.map((guest, index) => {
              const checkedIn = isGuestCheckedIn(guest);
              return (
                <Box key={guest.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    sx={{
                      py: 2,
                      px: 2,
                      bgcolor: checkedIn ? 'success.50' : 'transparent',
                      '&:active': {
                        bgcolor: 'action.selected',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body1" fontWeight={600}>
                            {guest.name}
                          </Typography>
                          {guest.rsvpStatus && guest.rsvpStatus !== 'PENDING' && (
                            <Chip
                              label={guest.rsvpStatus}
                              size="small"
                              variant="outlined"
                              color={
                                guest.rsvpStatus === 'YES'
                                  ? 'success'
                                  : guest.rsvpStatus === 'NO'
                                    ? 'error'
                                    : 'default'
                              }
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                          )}
                        </Stack>
                      }
                      secondary={guest.email}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <ListItemSecondaryAction>
                      {checkedIn ? (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
                          <IconButton
                            size="small"
                            onClick={() => handleUndoCheckIn(guest.id, guest.name)}
                            disabled={undoCheckIn.isPending}
                          >
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <IconButton
                          size="large"
                          color="primary"
                          onClick={() => handleManualCheckIn(guest.id, guest.name)}
                          disabled={manualCheckIn.isPending}
                          sx={{
                            p: 1.5,
                            '&:hover': { bgcolor: 'primary.50' },
                          }}
                        >
                          {manualCheckIn.isPending ? (
                            <CircularProgress size={28} />
                          ) : (
                            <RadioButtonUncheckedIcon sx={{ fontSize: 28 }} />
                          )}
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                </Box>
              );
            })}
          </List>
        )}
      </Card>
    </Box>
  );
}
